import React, { useRef, useEffect, useImperativeHandle, useState, useCallback, forwardRef } from 'react';
import Webcam from 'react-webcam';
import './WebcamCapture.css';

/**
 * react-webcam exposes .video on its ref at runtime but the published types
 * omit it. Narrow at the call site instead of patching the upstream package.
 */
function getWebcamVideo(webcam: Webcam | null): HTMLVideoElement | null {
  if (!webcam || !('video' in webcam)) return null;
  const video = webcam.video;
  return video instanceof HTMLVideoElement ? video : null;
}

export interface VisibleFrame {
  readonly imageData: ImageData;
  readonly width: number;
  readonly height: number;
  toJpegDataUrl(): string;
  toPngDataUrl(): string;
}

export interface WebcamViewportHandle {
  getVisibleFrame(): VisibleFrame | null;
  /** Current camera frame. Does not crop to the screen or copy pixels with getImageData. */
  captureFullFrameCanvas(): HTMLCanvasElement | null;
  flash(): void;
}

interface WebcamViewportProps {
  readonly className?: string;
  readonly children?: React.ReactNode;
  readonly overlay?: React.ReactNode;
  /** Control pinned to the top-right corner of the preview (e.g. close camera). */
  readonly cornerAction?: React.ReactNode;
  readonly onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * Item and box-size scans keep the browser's default camera mode and a screen-sized frame.
   * Packing photos turn this on for a 1920×1440, 30 fps
   * camera mode. The JPEG encode still scales a file that is over 3 MB.
   */
  readonly fullResolution?: boolean;
}

/** Scan previews stay on the browser default so barcode and QR can decode every frame. */
const SCAN_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: 'environment',
};

/** 4:3 still that these warehouse cameras can deliver at 30 fps, under the 3 MB JPEG. */
const VIEWFINDER_WIDTH = 1920;
const VIEWFINDER_HEIGHT = 1440;
const VIEWFINDER_FRAME_RATE = 30;

/** `ideal` so a browser that does not know resizeMode still opens the camera. */
const RESIZE_MODE_NONE = { ideal: 'none' } as const;

interface UncroppedVideoConstraints extends MediaTrackConstraints {
  readonly resizeMode: typeof RESIZE_MODE_NONE;
}

const FULL_VIDEO_CONSTRAINTS = {
  facingMode: 'environment',
  width: { ideal: VIEWFINDER_WIDTH },
  height: { ideal: VIEWFINDER_HEIGHT },
  frameRate: { ideal: VIEWFINDER_FRAME_RATE },
  resizeMode: RESIZE_MODE_NONE,
} satisfies UncroppedVideoConstraints;

interface CoverSourceRect {
  readonly sx: number;
  readonly sy: number;
  readonly sw: number;
  readonly sh: number;
}

function coverSourceRect(
  sourceWidth: number,
  sourceHeight: number,
  destWidth: number,
  destHeight: number,
): CoverSourceRect {
  const destAspect = destWidth / destHeight;
  const sourceAspect = sourceWidth / sourceHeight;
  if (sourceAspect > destAspect) {
    const sw = sourceHeight * destAspect;
    return { sx: (sourceWidth - sw) / 2, sy: 0, sw, sh: sourceHeight };
  }
  const sh = sourceWidth / destAspect;
  return { sx: 0, sy: (sourceHeight - sh) / 2, sw: sourceWidth, sh };
}

function backingStoreSize(
  cropWidth: number,
  cropHeight: number,
  cssWidth: number,
  cssHeight: number,
): { width: number; height: number } {
  const dpr = window.devicePixelRatio || 1;
  const scale = Math.max(1, (cssWidth * dpr) / cropWidth, (cssHeight * dpr) / cropHeight);
  return {
    width: Math.max(1, Math.round(cropWidth * scale)),
    height: Math.max(1, Math.round(cropHeight * scale)),
  };
}

function cachedContext(
  canvas: HTMLCanvasElement,
  cache: { current: CanvasRenderingContext2D | null },
): CanvasRenderingContext2D | null {
  if (cache.current && cache.current.canvas === canvas) {
    return cache.current;
  }
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  cache.current = ctx;
  return ctx;
}

function drawVideoCrop(
  ctx: CanvasRenderingContext2D,
  video: CanvasImageSource,
  crop: CoverSourceRect,
  destWidth: number,
  destHeight: number,
): void {
  ctx.drawImage(video, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, destWidth, destHeight);
}

/** Whole camera frame, sized to fit the stage without cropping either axis. */
function containedStoreSize(
  sourceWidth: number,
  sourceHeight: number,
  cssWidth: number,
  cssHeight: number,
): { width: number; height: number } {
  const dpr = window.devicePixelRatio || 1;
  const scale = Math.min(cssWidth / sourceWidth, cssHeight / sourceHeight);
  return {
    width: Math.max(1, Math.round(sourceWidth * scale * dpr)),
    height: Math.max(1, Math.round(sourceHeight * scale * dpr)),
  };
}

function drawFullSensorCanvas(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  ctxCache: { current: CanvasRenderingContext2D | null },
): HTMLCanvasElement | null {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (width < 1 || height < 1) return null;
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const ctx = cachedContext(canvas, ctxCache);
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, width, height);
  return canvas;
}

function visibleFrame(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): VisibleFrame {
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return {
    imageData,
    width: canvas.width,
    height: canvas.height,
    toJpegDataUrl: (): string => canvas.toDataURL('image/jpeg'),
    toPngDataUrl: (): string => canvas.toDataURL('image/png'),
  };
}

export const WebcamViewport = forwardRef<WebcamViewportHandle, WebcamViewportProps>(
  function WebcamViewport(
    { className, children, overlay, cornerAction, onClick, fullResolution = false },
    ref,
  ): React.ReactElement {
    const webcamRef = useRef<Webcam>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
    const captureCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const captureCtxRef = useRef<CanvasRenderingContext2D | null>(null);
    const [scanFlashId, setScanFlashId] = useState(0);
    const flash = useCallback((): void => {
      setScanFlashId((id) => id + 1);
    }, []);

    useEffect(() => {
      let rafId: number | null = null;

      const tick = (): void => {
        const video = getWebcamVideo(webcamRef.current);
        const wrapper = wrapperRef.current;
        const canvas = canvasRef.current;
        if (!video || !wrapper || !canvas || video.readyState < 2) {
          rafId = requestAnimationFrame(tick);
          return;
        }

        const vw = video.videoWidth;
        const vh = video.videoHeight;
        const cssW = wrapper.clientWidth;
        const cssH = wrapper.clientHeight;
        if (!vw || !vh || !cssW || !cssH) {
          rafId = requestAnimationFrame(tick);
          return;
        }

        const crop = coverSourceRect(vw, vh, cssW, cssH);
        const backing = fullResolution
          ? containedStoreSize(vw, vh, cssW, cssH)
          : backingStoreSize(crop.sw, crop.sh, cssW, cssH);
        if (canvas.width !== backing.width || canvas.height !== backing.height) {
          canvas.width = backing.width;
          canvas.height = backing.height;
        }

        const ctx = cachedContext(canvas, ctxRef);
        if (ctx) {
          if (fullResolution) {
            ctx.drawImage(video, 0, 0, backing.width, backing.height);
          } else {
            drawVideoCrop(ctx, video, crop, backing.width, backing.height);
          }
        }

        rafId = requestAnimationFrame(tick);
      };

      rafId = requestAnimationFrame(tick);
      return () => {
        if (rafId) cancelAnimationFrame(rafId);
      };
    }, [fullResolution]);

    useImperativeHandle(
      ref,
      () => {
        const handle: WebcamViewportHandle = {
        captureFullFrameCanvas(): HTMLCanvasElement | null {
          const video = getWebcamVideo(webcamRef.current);
          if (!video || video.readyState < 2) return null;
          if (!video.videoWidth || !video.videoHeight) return null;
          const canvas = captureCanvasRef.current ?? document.createElement('canvas');
          captureCanvasRef.current = canvas;
          return drawFullSensorCanvas(video, canvas, captureCtxRef);
        },
        getVisibleFrame(): VisibleFrame | null {
          if (fullResolution) {
            const canvas = handle.captureFullFrameCanvas();
            if (!canvas) return null;
            const ctx = cachedContext(canvas, captureCtxRef);
            if (!ctx) return null;
            return visibleFrame(canvas, ctx);
          }

          const canvas = canvasRef.current;
          if (!canvas || canvas.width < 1 || canvas.height < 1) return null;
          const ctx = cachedContext(canvas, ctxRef);
          if (!ctx) return null;
          return visibleFrame(canvas, ctx);
        },
        flash,
        };
        return handle;
      },
      [flash, fullResolution],
    );

    const wrapperClass = className ? `webcam-wrapper ${className}` : 'webcam-wrapper';

    return (
      <div ref={wrapperRef} className={wrapperClass} onClick={onClick}>
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          className={fullResolution ? 'webcam-video-source webcam-video-source-full' : 'webcam-video-source'}
          videoConstraints={fullResolution ? FULL_VIDEO_CONSTRAINTS : SCAN_VIDEO_CONSTRAINTS}
        />
        <canvas
          ref={canvasRef}
          className={fullResolution ? 'webcam-viewport-canvas webcam-viewport-canvas-full' : 'webcam-viewport-canvas'}
        />
        {scanFlashId > 0 ? <div key={scanFlashId} className="webcam-scan-flash" aria-hidden="true" /> : null}
        {children}
        {cornerAction ? <div className="webcam-viewport-corner">{cornerAction}</div> : null}
        {overlay ? (
          <div className="webcam-viewport-overlay" aria-live="polite">
            {overlay}
          </div>
        ) : null}
      </div>
    );
  },
);
